<template>
  <div class="min-h-screen bg-[var(--color-bg-primary)]">
    <!-- Sidebar -->
    <aside
      :inert="!sidebarOpen"
      class="fixed inset-y-0 start-0 z-50 w-64 flex flex-col glass-sidebar text-white transform ease-out"
      :class="[
        sidebarOpen ? 'translate-x-0' : 'ltr:-translate-x-full rtl:translate-x-full',
        transitionsReady ? 'transition-transform duration-300 motion-reduce:transition-none' : 'transition-none',
      ]"
    >
      <!-- Logo -->
      <div class="h-16 px-5 flex items-center border-b border-[var(--color-brand-primary-light)]/30">
        <NuxtLink to="/" class="flex items-center gap-2">
          <img src="/brand/symbol-on-dark.svg" alt="" class="h-7 w-7 shrink-0" />
          <span class="font-[var(--font-display)] text-[15px] font-bold tracking-normal text-white">
            ALNAKIB<span class="text-[var(--color-brand-secondary-light)]">4</span>TRADE
          </span>
        </NuxtLink>
        <button type="button" :aria-label="$t('ui.sidebar.close')" @click="closeSidebar()" class="ms-auto lg:hidden min-w-11 min-h-11 flex items-center justify-center text-[var(--color-text-muted-light)] hover:text-white transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" strokeLinejoin="round" stroke-width="1.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Nav label -->
      <div class="px-5 pt-5 pb-2">
        <span class="text-[10px] font-medium tracking-[0.2em] uppercase text-[var(--color-text-muted-light)]">
          {{ $t('nav.navigation') }}
        </span>
      </div>

      <!-- Nav items -->
      <nav class="flex-1 min-h-0 px-3 pb-4 space-y-0.5 overflow-y-auto scrollbar-thin">
        <template v-for="item in navItems" :key="item.path || item.group">
          <!-- Single nav link (no children) -->
          <NuxtLink
            v-if="!item.children"
            :to="item.path!"
            class="flex min-h-11 items-center gap-3 px-3 py-2.5 text-[13px] rounded-[var(--radius-md)] transition-all duration-[var(--duration-fast)]"
            :class="[
              isActive(item.path!)
                ? 'bg-[var(--color-brand-secondary)]/10 text-[var(--color-brand-secondary-light)] border-s-2 border-[var(--color-brand-secondary)] -ms-px'
                : 'text-[var(--color-text-muted-light)] hover:text-white hover:bg-white/5'
            ]"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span class="w-5 h-5 flex-shrink-0" v-html="item.icon"></span>
            <span>{{ item.label }}</span>
          </NuxtLink>

          <!-- Collapsible group -->
          <div v-else>
            <button
               :aria-expanded="expandedGroups.has(item.group!)"
              class="w-full min-h-11 flex items-center gap-3 px-3 py-2.5 text-[13px] rounded-[var(--radius-md)] transition-all duration-[var(--duration-fast)]"
              :class="[
                isGroupActive(item.children)
                  ? 'text-[var(--color-brand-secondary-light)]'
                  : 'text-[var(--color-text-muted-light)] hover:text-white hover:bg-white/5'
              ]"
              @click="toggleGroup(item.group!)"
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span class="w-5 h-5 flex-shrink-0" v-html="item.icon"></span>
              <span class="flex-1 text-start">{{ item.label }}</span>
              <svg class="w-3.5 h-3.5 transition-transform duration-[var(--duration-fast)]" :class="{ 'rotate-90': expandedGroups.has(item.group!) }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" strokeLinejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <div v-show="expandedGroups.has(item.group!)" class="space-y-0.5">
              <NuxtLink
                v-for="child in item.children"
                :key="child.path"
                :to="child.path"
                class="flex min-h-11 items-center gap-3 ps-8 pe-3 py-2 text-[13px] rounded-[var(--radius-md)] transition-all duration-[var(--duration-fast)]"
                :class="[
                  isActive(child.path)
                    ? 'bg-[var(--color-brand-secondary)]/10 text-[var(--color-brand-secondary-light)] border-s-2 border-[var(--color-brand-secondary)] -ms-px'
                    : 'text-[var(--color-text-muted-light)] hover:text-white hover:bg-white/5'
                ]"
              >
                <span class="w-1.5 h-1.5 rounded-full" :class="isActive(child.path) ? 'bg-[var(--color-brand-secondary-light)]' : 'bg-[var(--color-brand-primary-light)]'"></span>
                <span>{{ child.label }}</span>
              </NuxtLink>
            </div>
          </div>
        </template>
      </nav>

      <!-- Bottom user -->
      <div class="shrink-0 p-4 border-t border-[var(--color-brand-primary-light)]/30">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-[var(--color-brand-secondary)]/20 text-[var(--color-brand-secondary-light)] rounded-full flex items-center justify-center text-sm font-bold">
            {{ userInitials }}
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm text-white truncate font-medium">{{ user?.profile?.firstName || $t('common.admin') }}</p>
            <p class="text-xs text-[var(--color-text-muted-light)] truncate">{{ user?.email }}</p>
          </div>
        </div>
      </div>
    </aside>

    <!-- Main -->
    <div class="lg:ms-64 min-h-screen">
      <!-- Top bar -->
      <header class="sticky top-0 z-40 h-16 bg-[var(--color-surface-primary)]/75 backdrop-blur-xl backdrop-saturate-[1.15] border-b border-[var(--color-border-primary)]/50 flex items-center justify-between px-2 sm:px-5 transition-all duration-200">
        <div class="flex items-center gap-4">
          <button type="button" :aria-label="$t('ui.sidebar.toggle')" :aria-expanded="sidebarOpen ? 'true' : 'false'" @click="sidebarOpen = !sidebarOpen" class="lg:hidden min-w-11 min-h-11 p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" strokeLinejoin="round" stroke-width="1.5" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <UiBreadcrumb class="hidden lg:flex" />
        </div>

        <div class="flex items-center gap-1 sm:gap-3">
          <LanguageSwitcher />
          <!-- Notification Bell -->
          <div class="relative" ref="notifRef">
            <button
              @click="notifOpen = !notifOpen"
              :aria-label="$t('common.notifications')"
              :aria-expanded="notifOpen"
              class="relative text-[var(--color-text-muted)] hover:text-[var(--color-brand-secondary)] transition-colors p-2 rounded-[var(--radius-md)] hover:bg-[var(--color-surface-secondary)]"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" strokeLinejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span
                v-if="unreadCount > 0"
                class="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-status-error rounded-full ring-2 ring-[var(--color-surface-primary)]"
              >
                {{ unreadCount > 99 ? '99+' : unreadCount }}
              </span>
            </button>

            <!-- Notification Dropdown -->
            <Transition
              enter-active-class="transition ease-out duration-150"
              enter-from-class="opacity-0 scale-95 translate-y-1"
              enter-to-class="opacity-100 scale-100 translate-y-0"
              leave-active-class="transition ease-in duration-100"
              leave-from-class="opacity-100 scale-100 translate-y-0"
              leave-to-class="opacity-0 scale-95 translate-y-1"
            >
              <div
                v-if="notifOpen"
                class="absolute end-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-[var(--color-surface-primary)] border border-[var(--color-border-primary)] rounded-[var(--radius-lg)] shadow-xl z-50 overflow-hidden"
              >
                <div class="flex items-center justify-between px-4 py-3 border-b border-[var(--color-border-primary)]">
                  <h3 class="text-sm font-semibold text-text-primary">{{ $t('common.notifications') }}</h3>
                  <button
                    v-if="unreadCount > 0"
                    @click="markAllRead"
                    class="text-xs text-brand-secondary hover:underline"
                  >
                    {{ $t('common.markAllRead') }}
                  </button>
                </div>
                <div class="max-h-80 overflow-y-auto divide-y divide-[var(--color-border-primary)]">
                  <button type="button"
                    v-for="notif in notifications"
                    :key="notif.id"
                    class="w-full text-start px-4 py-3 hover:bg-[var(--color-surface-secondary)] transition-colors cursor-pointer"
                    :class="{ 'bg-status-info-light/30': !notif.read }"
                    @click="handleNotifClick(notif)"
                  >
                    <div class="flex gap-3">
                      <div class="flex-shrink-0 mt-0.5">
                        <div
                          class="w-8 h-8 rounded-full flex items-center justify-center"
                          :class="notifIconClass(notif.type)"
                        >
                          <svg v-if="notif.type === 'order'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>
                          <svg v-else-if="notif.type === 'payment'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>
                          <svg v-else-if="notif.type === 'shipment'" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"/></svg>
                          <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                        </div>
                      </div>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm text-text-primary line-clamp-2" :class="{ 'font-medium': !notif.read }">{{ notif.message }}</p>
                        <p class="text-xs text-text-muted mt-0.5">{{ formatTimeAgo(notif.createdAt) }}</p>
                      </div>
                      <span v-if="!notif.read" class="flex-shrink-0 mt-2 w-2 h-2 rounded-full bg-brand-secondary"></span>
                    </div>
                  </button>
                  <div v-if="notifications.length === 0" class="px-4 py-8 text-center">
                    <svg class="w-8 h-8 mx-auto text-text-muted mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <p class="text-sm text-text-muted">{{ $t('common.noNotifications') }}</p>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
          <div class="h-5 w-px bg-[var(--color-border-primary)]"></div>
          <button type="button" :aria-label="$t('common.logout')" @click="logout()" class="inline-flex min-h-10 min-w-10 items-center justify-center text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] tracking-wide uppercase transition-colors font-medium">
            <svg class="h-5 w-5 sm:hidden" aria-hidden="true" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4m7 14 5-5-5-5m5 5H9" /></svg>
            <span class="hidden whitespace-nowrap sm:inline">{{ $t('common.logout') }}</span>
          </button>
        </div>
      </header>

      <!-- Page content -->
      <main class="p-6 lg:p-8">
        <slot />
      </main>
    </div>

    <UiToastContainer />

    <!-- Mobile overlay -->
    <div
      v-if="sidebarOpen"
      class="fixed inset-0 z-40 bg-[var(--color-brand-primary-darker)]/60 backdrop-blur-sm lg:hidden"
      @click="closeSidebar()"
    ></div>
  </div>
</template>

<script setup lang="ts">
interface NavChild {
  path: string;
  label: string;
}

interface NavItem {
  path?: string;
  group?: string;
  label: string;
  icon: string;
  children?: NavChild[];
}

const route = useRoute();
const { user, logout } = useAuth();
const { t: $t, locale } = useI18n();
const { sidebarOpen, transitionsReady, closeSidebar } = useSidebarState();
const expandedGroups = reactive(new Set<string>());

watch(() => route.fullPath, () => {
  if (window.innerWidth < 1024) closeSidebar();
});

// Notifications
interface Notification {
  id: string;
  type: 'order' | 'payment' | 'shipment' | 'system';
  message: string;
  read: boolean;
  link?: string;
  createdAt: string;
}

const notifOpen = ref(false);
const notifRef = ref<HTMLElement | null>(null);
const notifications = ref<Notification[]>([]);
const notifLoading = ref(false);

const unreadCount = computed(() => notifications.value.filter(n => !n.read).length);

function notifIconClass(type: string) {
  const map: Record<string, string> = {
    order: 'bg-status-info-light text-status-info',
    payment: 'bg-status-success-light text-status-success',
    shipment: 'bg-status-warning-light text-status-warning',
    system: 'bg-surface-tertiary text-text-secondary',
  };
  return map[type] || map.system;
}

function formatTimeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return $t('common.justNow');
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

async function fetchNotifications() {
  try {
    notifLoading.value = true;
    const api = useApi();
    const res = await api.get('/notifications?limit=10&sort=-createdAt');
    // API returns { notifications: [...] } with localized message objects
    type ApiNotification = { _id: string; type: string; read: boolean; createdAt: string; actionUrl?: string; message?: Record<string, string> };
    const list = (res.data as { notifications?: ApiNotification[] } | undefined)?.notifications ?? [];
    notifications.value = list.map((n) => ({
      id: n._id,
      type: n.type.startsWith('order') ? 'order'
        : n.type.startsWith('payment') ? 'payment'
        : /ship|warehouse|package/.test(n.type) ? 'shipment'
        : 'system',
      message: n.message?.[locale.value] || n.message?.en || '',
      read: n.read,
      link: n.actionUrl,
      createdAt: n.createdAt,
    }));
  } catch {
    // Silently fail  -  bell just shows empty
  } finally {
    notifLoading.value = false;
  }
}

async function markAllRead() {
  try {
    const api = useApi();
    const result = await api.put('/notifications/read-all', {});
    if (result.success) notifications.value.forEach(n => n.read = true);
    else useToast().error(result.error?.message || $t('common.operationFailed'));
  } catch {
    // Silently fail
  }
}

async function handleNotifClick(notif: Notification): Promise<void> {
  if (!notif.read) {
    const result = await useApi().put(`/notifications/${encodeURIComponent(notif.id)}/read`, {});
    if (result.success) notif.read = true;
    else useToast().error(result.error?.message || $t('common.operationFailed'));
  }
  notifOpen.value = false;
  if (notif.link?.startsWith('/') && !notif.link.startsWith('//')) {
    const path = notif.link.replace(/^\/admin(?=\/|$)/, '') || '/';
    await navigateTo(path);
  }
}

// Click outside to close notification dropdown
function onClickOutside(e: MouseEvent) {
  if (notifRef.value && !notifRef.value.contains(e.target as Node)) {
    notifOpen.value = false;
  }
}

onMounted(() => {
  fetchNotifications();
  document.addEventListener('click', onClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside);
});

const userInitials = computed(() => {
  const f = user.value?.profile?.firstName?.[0] || '';
  const l = user.value?.profile?.lastName?.[0] || '';
  return (f + l).toUpperCase() || 'A';
});

function isActive(path: string) {
  if (path === '/') return route.path === '/';
  return route.path === path || route.path.startsWith(path + '/');
}

function isGroupActive(children: NavChild[]) {
  return children.some(c => isActive(c.path));
}

function toggleGroup(group: string) {
  if (expandedGroups.has(group)) {
    expandedGroups.delete(group);
  } else {
    expandedGroups.add(group);
  }
}

// SVG icon constants  -  must be declared before navItems which uses them
const icons = {
  dashboard: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/></svg>',
  warehouse: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"/></svg>',
  shipments: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"/></svg>',
  buyShip: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>',
  investigations: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>',
  users: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/></svg>',
  payments: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/></svg>',
  chat: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>',
  membership: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"/></svg>',
  directory: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>',
  returns: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/></svg>',
  orders: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/></svg>',
  content: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/></svg>',
  vendors: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>',
  settings: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>',
  products: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>',
  suppliers: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/></svg>',
  analytics: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>',
  reports: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>',
  support: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/></svg>',
  auditLogs: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>',
  promotions: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/></svg>',
  categories: '<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>',
};

const navItems = computed<NavItem[]>(() => [
  { path: '/', label: $t('nav.dashboard'), icon: icons.dashboard },
  { path: '/orders', label: $t('nav.orders'), icon: icons.orders },
  {
    group: 'products', label: $t('nav.products'), icon: icons.products,
    children: [
      { path: '/products', label: $t('nav.allProducts') },
      { path: '/products/add', label: $t('nav.addProduct') },
      { path: '/categories', label: $t('nav.categories') },
    ],
  },
  { path: '/purchase-orders', label: $t('nav.buyAndShip'), icon: icons.buyShip },
  {
    group: 'investigations', label: $t('nav.investigations'), icon: icons.investigations,
    children: [
      { path: '/investigations', label: $t('nav.allInvestigations') },
      { path: '/investigations/service-levels', label: $t('nav.serviceLevels') },
    ],
  },
  {
    group: 'warehouse', label: $t('nav.warehouse'), icon: icons.warehouse,
    children: [
      { path: '/warehouse', label: $t('nav.warehouseOperations') },
      { path: '/warehouse/receive', label: $t('nav.receivePackage') },
      { path: '/warehouse/inspections', label: $t('nav.inspections') },
    ],
  },
  {
    group: 'shipments', label: $t('nav.shipments'), icon: icons.shipments,
    children: [
      { path: '/shipments', label: $t('nav.allShipments') },
      { path: '/shipments/weighing-queue', label: $t('nav.weighingQueue') },
    ],
  },
  { path: '/returns', label: $t('nav.returns'), icon: icons.returns },
  {
    group: 'users', label: $t('nav.users'), icon: icons.users,
    children: [
      { path: '/users', label: $t('nav.allUsers') },
      { path: '/resellers', label: $t('nav.resellers') },
    ],
  },
  {
    group: 'suppliers', label: $t('nav.suppliers'), icon: icons.suppliers,
    children: [
      { path: '/suppliers', label: $t('nav.allSuppliers') },
      { path: '/suppliers/add', label: $t('nav.addSupplier') },
    ],
  },
  {
    group: 'vendors', label: $t('nav.vendors'), icon: icons.vendors,
    children: [
      { path: '/vendors', label: $t('nav.allVendors') },
      { path: '/vendors/applications', label: $t('nav.vendorApplications') },
      { path: '/vendors/products', label: $t('nav.vendorProducts') },
      { path: '/vendors/payouts', label: $t('nav.vendorPayouts') },
    ],
  },
  {
    group: 'payments', label: $t('nav.payments'), icon: icons.payments,
    children: [
      { path: '/payments', label: $t('nav.transactions') },
      { path: '/payments/bank-transfers', label: $t('nav.bankTransfers') },
      { path: '/payments/cash-payments', label: $t('nav.cashPayments') },
      { path: '/wallet', label: $t('nav.wallets') },
    ],
  },
  { path: '/chat', label: $t('nav.chat'), icon: icons.chat },
  {
    group: 'membership', label: $t('nav.membership'), icon: icons.membership,
    children: [
      { path: '/memberships', label: $t('nav.membershipDashboard') },
      { path: '/memberships/tiers', label: $t('nav.tiers') },
      { path: '/memberships/subscribers', label: $t('nav.subscribers') },
      { path: '/memberships/promotions', label: $t('nav.promotions') },
    ],
  },
  {
    group: 'promotions', label: $t('nav.promotions'), icon: icons.promotions,
    children: [
      { path: '/promotions', label: $t('nav.allPromotions') },
      { path: '/promotions/add', label: $t('nav.addPromotion') },
    ],
  },
  {
    group: 'directory', label: $t('nav.directory'), icon: icons.directory,
    children: [
      { path: '/directory', label: $t('nav.listings') },
      { path: '/directory/applications', label: $t('nav.applications') },
      { path: '/directory/reviews', label: $t('directoryManagement.reviewsTitle') },
    ],
  },
  {
    group: 'content', label: $t('nav.content'), icon: icons.content,
    children: [
      { path: '/content/pages', label: $t('nav.pages') },
      { path: '/content/posts', label: $t('nav.posts') },
      { path: '/content/categories', label: $t('nav.contentCategories') },
      { path: '/content/media', label: $t('nav.mediaLibrary') },
    ],
  },
  { path: '/support', label: $t('nav.support'), icon: icons.support },
  { path: '/analytics', label: $t('nav.analytics'), icon: icons.analytics },
  { path: '/reports', label: $t('nav.reports'), icon: icons.reports },
  { path: '/audit-logs', label: $t('nav.auditLogs'), icon: icons.auditLogs },
  {
    group: 'settings', label: $t('nav.settings'), icon: icons.settings,
    children: [
      { path: '/settings', label: $t('nav.overview') },
      { path: '/settings/branding', label: $t('settings.branding') },
      { path: '/settings/platform', label: $t('settings.platform') },
      { path: '/settings/marketplaces', label: $t('settings.marketplaces') },
      { path: '/settings/carriers', label: $t('settings.carriers') },
      { path: '/settings/shipping-rates', label: $t('settings.shippingRates') },
      { path: '/settings/shipping-restrictions', label: $t('settings.shippingRestrictions') },
      { path: '/settings/warehouses', label: $t('settings.warehouses') },
      { path: '/settings/fees', label: $t('settings.fees') },
      { path: '/settings/currency', label: $t('settings.currency') },
      { path: '/settings/payments', label: $t('settings.paymentsLabel') },
      { path: '/settings/collection-points', label: $t('settings.collectionPoints') },
      { path: '/settings/integrations', label: $t('settings.integrations') },
      { path: '/settings/notifications', label: $t('settings.notifications') },
      { path: '/settings/notification-templates', label: $t('settings.templates') },
      { path: '/settings/chat', label: $t('settings.chatSettings') },
      { path: '/settings/directory', label: $t('settings.directorySettings') },
      { path: '/settings/vendors', label: $t('settings.vendorSettings') },
      { path: '/settings/localization', label: $t('settings.localization') },
    ],
  },
]);

// Auto-expand groups that have an active child
function autoExpandActiveGroups() {
  for (const item of navItems.value) {
    if (item.children && item.group && isGroupActive(item.children)) {
      expandedGroups.add(item.group);
    }
  }
}

// Run during setup (not onMounted) so the active group is already open on first paint.
autoExpandActiveGroups();
watch(() => route.path, autoExpandActiveGroups);
</script>
